import React, { useState } from 'react';
import { 
  Settings, 
  Headphones, 
  Wrench, 
  HardDrive, 
  Cpu, 
  Package, 
  Network, 
  Users,
  Camera,
  CheckCircle2,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';

const Services = () => {
  const [activeService, setActiveService] = useState(null);

  const services = [
    {
      id: 1,
      title: "Manutenção Preventiva",
      description: "Gestão de TI para empresas com revisão sistemática, controle e monitoramento dos equipamentos para evitar falhas e garantir produtividade.",
      icon: Settings,
      color: "from-blue-500 to-blue-600",
      shadowColor: "shadow-blue-500/30",
      features: ["Limpeza interna", "Verificação de hardware", "Atualização de drivers", "Diagnóstico completo"]
    },
    {
      id: 2,
      title: "Suporte de TI para Empresas",
      description: "Suporte técnico empresarial completo com help desk TI, atendimento rápido e eficiente para sua infraestrutura tecnológica.",
      icon: Headphones,
      color: "from-cyan-500 to-cyan-600",
      shadowColor: "shadow-cyan-500/30",
      features: ["Suporte remoto de TI", "Atendimento presencial", "Help Desk TI", "Monitoramento 24h"]
    },
    {
      id: 3,
      title: "Manutenção Corretiva",
      description: "Serviços de TI para empresas com reparo especializado em computadores, notebooks e periféricos com garantia.",
      icon: Wrench,
      color: "from-orange-500 to-orange-600",
      shadowColor: "shadow-orange-500/30",
      features: ["Reparo de hardware", "Troca de componentes", "Recuperação de dados", "Garantia de serviço"]
    },
    {
      id: 4,
      title: "Formatação",
      description: "Backup completo, limpeza de disco rígido e instalação do Microsoft Windows© com todos os drivers e programas essenciais.",
      icon: HardDrive,
      color: "from-purple-500 to-purple-600",
      shadowColor: "shadow-purple-500/30",
      features: ["Backup de dados", "Instalação Windows", "Drivers e programas", "Otimização do sistema"]
    },
    {
      id: 5,
      title: "Instalação & Upgrade",
      description: "Instalação profissional de componentes como SSD, HD, Memória RAM e placas de vídeo para melhorar a performance.",
      icon: Cpu,
      color: "from-green-500 to-green-600",
      shadowColor: "shadow-green-500/30",
      features: ["Upgrade de memória", "Instalação de SSD", "Troca de processador", "Placa de vídeo"]
    },
    {
      id: 6,
      title: "Terceirização de TI",
      description: "TI terceirizada completa com gestão de TI para empresas, incluindo sistemas ERP, emissor de NF-e e suporte contínuo.",
      icon: Package,
      color: "from-indigo-500 to-indigo-600",
      shadowColor: "shadow-indigo-500/30",
      features: ["Nota Fiscal Eletrônica", "Controle de estoque", "Gestão financeira", "Relatórios gerenciais"]
    },
    {
      id: 7,
      title: "Redes & Infraestrutura",
      description: "Instalação, configuração e manutenção de redes corporativas com suporte remoto de TI especializado.",
      icon: Network,
      color: "from-teal-500 to-teal-600",
      shadowColor: "shadow-teal-500/30",
      features: ["Cabeamento estruturado", "Configuração de roteadores", "Redes Wi-Fi", "Firewall e segurança"]
    },
    {
      id: 8,
      title: "Consultoria de TI",
      description: "Consultoria de TI especializada para otimização de processos, planejamento de infraestrutura e redução de custos empresariais.",
      icon: Users,
      color: "from-pink-500 to-pink-600",
      shadowColor: "shadow-pink-500/30",
      features: ["Análise de ambiente", "Planejamento estratégico", "Redução de custos", "Soluções personalizadas"]
    },
    {
      id: 9,
      title: "Câmeras, Alarmes e Wi-Fi Intelbras",
      description: "Instalação e configuração de câmeras de segurança CFTV, sistemas de alarme e redes Wi-Fi corporativas da marca Intelbras para empresas e residências.",
      icon: Camera,
      color: "from-slate-600 to-slate-700",
      shadowColor: "shadow-slate-500/30",
      features: ["Câmeras de segurança CFTV", "Sistemas de alarme Intelbras", "Wi-Fi corporativo Intelbras", "Monitoramento remoto via app"]
    }
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contato');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="servicos" className="py-20 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden" style={{ scrollMarginTop: '100px' }}>
      {/* Background decorativo */}
      <div className="absolute inset-0 opacity-10 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl"></div>
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-5 overflow-hidden pointer-events-none">
        <div className="w-full h-full" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header da seção */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center gap-2 mb-4 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full">
            <Sparkles className="w-5 h-5 text-blue-400" />
            <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">Serviços de TI para Empresas</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Nossos <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">Serviços</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Empresa de TI especializada em suporte técnico empresarial, terceirização de TI e consultoria de TI para sua empresa crescer com tecnologia
          </p>
        </div>

        {/* Grid de serviços */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {services.map((service) => {
            const Icon = service.icon;
            const isActive = activeService === service.id;
            
            return (
              <div
                key={service.id}
                className={`group relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 transition-all duration-500 cursor-pointer ${
                  isActive ? 'border-cyan-500 shadow-xl shadow-cyan-500/20 -translate-y-2' : 'hover:border-slate-600 hover:-translate-y-1'
                }`}
                onMouseEnter={() => setActiveService(service.id)}
                onMouseLeave={() => setActiveService(null)}
              >
                {/* Ícone */}
                <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-5 transition-all duration-300 ${service.shadowColor} shadow-lg group-hover:scale-110`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>

                {/* Título */}
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                  {service.title}
                </h3>

                {/* Descrição */}
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>

                {/* Features - aparece no hover */}
                <div className={`space-y-2 transition-all duration-300 ${isActive ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0 overflow-hidden'}`}>
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Indicador de hover */}
                <div className={`absolute bottom-4 right-4 transition-all duration-300 ${isActive ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
                  <ArrowRight className="w-5 h-5 text-cyan-400" />
                </div>

                {/* Borda gradiente animada */}
                <div className={`absolute inset-0 rounded-2xl transition-opacity duration-300 pointer-events-none ${isActive ? 'opacity-100' : 'opacity-0'}`}>
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12">
          <div className="bg-gradient-to-r from-slate-800/50 via-slate-800/80 to-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 md:p-12 max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Precisa de suporte de TI para sua empresa?
            </h3>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Somos uma empresa de TI especializada em serviços de TI para empresas. Solicite um orçamento de terceirização de TI ou consultoria de TI sem compromisso. 
              Atendemos empresas em Ribeirão Preto e região.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-bold px-8 py-6 text-lg shadow-xl shadow-blue-500/30 hover:scale-105 transition-all duration-300"
              >
                Solicitar Orçamento
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => window.open('https://wa.me/5516991378312?text=' + encodeURIComponent('Olá! Gostaria de saber mais sobre os serviços de TI para empresas.'), '_blank')}
                size="lg"
                variant="outline"
                className="border-2 border-green-500 text-green-400 hover:bg-green-500/10 font-bold px-8 py-6 text-lg hover:scale-105 transition-all duration-300"
              >
                Chamar no WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
